import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, ChevronDown, ClipboardCheck, MinusCircle, XCircle } from 'lucide-react';
import { importanceLabel } from '../pages/jobDescription/benchmarkLabels';

const APTIVIEW_GRADIENT = 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)';

function levelLabel(level) {
  if (level === 'above_expected') return 'Exceeds requirement';
  if (level === 'meets_expected') return 'Meets requirement';
  if (level === 'below_expected') return 'Below requirement';
  return 'Insufficient evidence';
}

function criterionIcon(item) {
  if (!item.evidence_sufficient || item.score == null) {
    return <MinusCircle className="w-4 h-4 text-amber-500 shrink-0" />;
  }
  if (item.performance_level === 'below_expected' || (item.min_score != null && Number(item.score) < Number(item.min_score))) {
    return <XCircle className="w-4 h-4 text-red-500 shrink-0" />;
  }
  if (item.performance_level === 'above_expected') {
    return <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />;
  }
  return <CheckCircle className="w-4 h-4 text-blue-600 shrink-0" />;
}

function formatNameList(names) {
  return (names || []).map((name) => {
    if (!name) return name;
    return name.charAt(0).toUpperCase() + name.slice(1);
  }).join(', ');
}

export default function BenchmarkScorecard({ benchmarks }) {
  const [openSlug, setOpenSlug] = useState(null);
  if (!benchmarks || benchmarks.status !== 'ready') return null;

  const overall = benchmarks.overall_score != null ? Number(benchmarks.overall_score).toFixed(1) : '—';

  return (
    <div className="pdf-page-break-before rounded-xl p-4 sm:p-5 border border-sky-100 bg-gradient-to-br from-sky-50/50 via-white to-fuchsia-50/40">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 min-w-0">
          <div className="p-2 rounded-lg shrink-0" style={{ background: APTIVIEW_GRADIENT }}>
            <ClipboardCheck className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-900">Interview Benchmarks</h2>
            <p className="text-gray-600 text-xs">How this candidate measured against the approved criteria for this role. Overall is a weighted average of criteria with enough evidence.</p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-xl font-bold text-sky-600">
            {overall}
            <span className="text-sm font-semibold text-gray-400">/10</span>
          </div>
          <div className="text-xs text-gray-500">
            {benchmarks.coverage_pct != null ? `${benchmarks.coverage_pct}% evidence coverage` : 'Evidence coverage'}
          </div>
        </div>
      </div>

      {benchmarks.critical_failures?.length > 0 && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50/80 px-3 py-2.5 text-sm text-red-800 flex gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">Required criterion not met</div>
            {benchmarks.critical_failures.map((row) => (
              <div key={row.slug} className="text-xs mt-0.5">
                {row.name}: scored {row.score}/10 (minimum {row.min_score})
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 mb-4">
        <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-gray-900">{benchmarks.critical_passed}/{benchmarks.critical_total}</div>
          <div className="text-[11px] text-gray-500 mt-0.5">Required criteria met</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-gray-900">{benchmarks.evidenced_count}/{benchmarks.total_count}</div>
          <div className="text-[11px] text-gray-500 mt-0.5">Criteria assessed</div>
        </div>
      </div>

      {(benchmarks.insufficient || []).length > 0 && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
          Insufficient evidence to assess: {formatNameList(benchmarks.insufficient)}
        </div>
      )}

      <div className="space-y-2">
        {(benchmarks.items || []).map((item) => {
          const open = openSlug === item.slug;
          const hasDetail = item.strength || item.gap || (item.skill_breakdown || []).length > 0 || (item.evidence || []).length > 0;
          return (
            <button
              type="button"
              key={item.slug}
              onClick={() => hasDetail && setOpenSlug(open ? null : item.slug)}
              className="w-full text-left bg-white border border-gray-200 rounded-lg px-3 py-2.5 hover:border-sky-200 transition-colors"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  {criterionIcon(item)}
                  <span className="font-medium text-sm text-gray-900 truncate">{item.name}</span>
                  <span className="hidden sm:inline text-[10px] font-medium text-gray-400 shrink-0">
                    {importanceLabel(item.importance)}
                    {item.weight_bps != null ? ` · weight ${Math.round(Number(item.weight_bps) / 100)}` : ''}
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="text-right">
                    <span className="text-sm font-semibold text-gray-900">
                      {item.score != null ? `${item.score}/10` : '—'}
                    </span>
                    <span className="ml-2 text-[11px] font-normal text-gray-500">
                      {levelLabel(item.performance_level)}
                    </span>
                  </div>
                  {hasDetail ? (
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
                  ) : null}
                </div>
              </div>
              {open && hasDetail && (
                <div className="mt-2.5 pt-2 border-t border-gray-100 text-xs text-gray-600 space-y-1.5">
                  {item.strength ? (
                    <div><span className="font-semibold text-gray-800">Strength: </span>{item.strength}</div>
                  ) : null}
                  {item.gap ? (
                    <div><span className="font-semibold text-gray-800">Development area: </span>{item.gap}</div>
                  ) : null}
                  {(item.skill_breakdown || []).length > 0 && (
                    <div>
                      <span className="font-semibold text-gray-800">Related skills: </span>
                      {item.skill_breakdown.map((s) => `${s.skill} (${s.score})`).join(', ')}
                    </div>
                  )}
                  {(item.evidence || []).length > 0 && (
                    <ul className="list-disc pl-4 space-y-0.5">
                      {item.evidence.map((ev, i) => <li key={i}>{ev}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
